/**
 * imports
 */
import { createApp } from 'vue'
// import { createStore } from 'vuex'
import { createPinia } from 'pinia'
import { useCollectionStore } from './vue/pinia/CollectionStore'
import './scss/main/main.scss'

import Flickity from 'vue-flickity'
import vSelect from "vue-select"
import VueLazyload from 'vue-lazyload'

// Visitor campaign mode is intentionally page-local: no cookie or storage key.
// A launch URL may opt in with ?mode=dark; ordinary navigation starts fresh.
const initialiseHomepageMode = () => {
  const hero = document.querySelector('.hp-hero')
  if (!hero) return
  const root = document.documentElement
  const mobile = window.matchMedia('(max-width: 768px)')
  const sizeHeroes = () => {
    document.querySelectorAll('.hp-hero').forEach(element => {
      const stage = element.querySelector('.hp-hero__stage')
      if (!stage) return
      const css = getComputedStyle(element)
      const lightHeight = element.clientWidth * parseFloat(css.getPropertyValue(mobile.matches ? '--hp-hero-stage-h-m' : '--hp-hero-stage-h')) / (mobile.matches ? 393 : 1400)
      const image = element.querySelector('.hp-hero__campaign img')
      const darkHeight = image?.naturalWidth ? element.clientWidth * image.naturalHeight / image.naturalWidth : lightHeight
      const overhang = parseFloat(css.getPropertyValue(mobile.matches ? '--hp-hero-overhang-m' : '--hp-hero-overhang')) || 0
      // Keep each composition at its natural size. Only the outer section changes
      // height, so the following content travels with its bottom edge.
      element.style.setProperty('--hp-hero-light-height', `${lightHeight}px`)
      element.style.setProperty('--hp-hero-mode-height', `${root.dataset.hpDark === 'true' && image ? darkHeight : lightHeight + overhang}px`)
      element.setAttribute('data-mode-sized', '')
    })
  }
  const apply = dark => {
    // CSS selects the saved palette and preserves each section's normal colours.
    root.dataset.hpDark = String(dark)
    root.dataset.campaign = dark ? 'on' : 'off'
    document.querySelectorAll('[data-hp-dark-toggle]').forEach(button => {
      button.setAttribute('aria-pressed', String(dark))
    })
    document.querySelectorAll('.hp-hero').forEach(element => {
      const campaign = element.querySelector('.hp-hero__campaign')
      if (!campaign) return
      campaign.inert = !dark
      campaign.setAttribute('aria-hidden', String(!dark))
      element.querySelectorAll('.hp-hero__headline, .hp-hero__float:not(.hp-hero__toggle), .hp-hero__cta').forEach(child => {
        child.inert = dark
        child.setAttribute('aria-hidden', String(dark))
      })
    })
    sizeHeroes()
  }
  apply(new URLSearchParams(window.location.search).get('mode') === 'dark' || root.dataset.campaign === 'on')
  document.addEventListener('click', event => {
    if (!event.target.closest('[data-hp-dark-toggle]')) return
    apply(root.dataset.hpDark !== 'true')
  })
  document.addEventListener('hp:editor:section-load', () => apply(root.dataset.hpDark === 'true'))
  // Observing width only avoids a height-animation feedback loop.
  let width = 0
  const observer = new ResizeObserver(entries => {
    const next = entries[0].contentRect.width
    if (next === width) return
    width = next
    sizeHeroes()
  })
  observer.observe(document.documentElement)
  document.addEventListener('load', event => {
    if (event.target.matches?.('.hp-hero__campaign img')) sizeHeroes()
  }, true)
  mobile.addEventListener('change', sizeHeroes)
}

/**
 * create vue instance function
 */
let homepagePinia
const homepageProducts = new Map()
const collectHomepageProducts = () => {
  // Vue removes script nodes during template compilation; retain the same
  // server-rendered product payload before mounting, including editor updates.
  document.querySelectorAll('[data-hp-product-json]').forEach(node => {
    try {
      const product = JSON.parse(node.textContent)
      if (product.handle) homepageProducts.set(product.handle, product)
    } catch (error) {
      console.warn('[hp-quick-view] Invalid product payload; product URL remains available', error)
    }
  })
}
const createVueApp = (pinia = createPinia()) => {
  collectHomepageProducts()
  const app = createApp({})
  if (!homepagePinia) homepagePinia = pinia

   app.component('VueFlickity', Flickity)
   app.component("VSelect", vSelect)
   app.use(VueLazyload, {
     listenEvents: ['scroll', 'wheel', 'mousewheel', 'resize', 'touchmove'],
     preLoad: 1.3,
     observer: true,
     observerOptions: {
       rootMargin: '0px',
       threshold: 0.1
     }
   })

  /**
   * vue components
   * auto-import all vue components
   */
  const vueComponents = require.context('./vue/components/', true, /\.(vue|js)$/)

  vueComponents.keys().forEach(key => {
    const component = vueComponents(key).default

    // if a component has a name defined use the name, else use the path as the component name
    const name = component.name
      ? component.name
      : key.replace(/\.(\/|vue|js)/g, '').replace(/(\/|-|_|\s)\w/g, (match) => match.slice(1).toUpperCase())

    app.component(name, component)
  })

  /**
   * vue mixins
   * auto-register all mixins with a 'global' keyword in their filename
   */
  const mixins = require.context('./vue/mixins/', true, /.*global.*\.js$/)

  mixins.keys().forEach(key => {
    app.mixin(mixins(key).default)
  })

  /**
   * vue directives
   * auto-register all directives with a 'global' keyword in their filename
   */
  const directives = require.context('./vue/directives/', true, /.*global.*\.js$/)

  directives.keys().forEach(key => {
    const directive = directives(key).default
    app.directive(directive.name, directive.directive)
  })

  /**
   * vue plugins
   * extend with additional features
   */
  app.use(pinia)

  return app
}

/**
 * create and mount vue instance(s)
 */
const appElement = document.querySelector('#app')
const islandApps = new Map()
const islandIds = new Set()

if (appElement) {
  createVueApp().mount(appElement)
  // The index layout renders its sections outside #app; mount only those that use Vue.
  const homepageContent = document.querySelector('.template-index #MainContent')
  if (homepageContent && !appElement.contains(homepageContent)) {
    homepageContent.querySelectorAll('.shopify-section, [vue]').forEach(el => {
      if (!el.hasAttribute('vue') && [...el.children].every(child => child.hasAttribute('v-pre'))) return
      const app = createVueApp(homepagePinia)
      app.mount(el)
      if (el.id) {
        const sectionId = el.id.replace('shopify-section-', '')
        islandApps.set(sectionId, { el, app })
        islandIds.add(sectionId)
      }
    })
  }
} else {
  const vueElements = document.querySelectorAll('[vue]')
  if (vueElements) vueElements.forEach(el => createVueApp().mount(el))
}

initialiseHomepageMode()

// Vue replaces the initial DOM. Rebind the merchant's existing TrustBox after
// mount and page load so a bootstrap that ran earlier cannot leave an orphan iframe.
const initialiseHomepageTrustpilot = () => {
  if (!document.querySelector('.hp-hero') || !window.Trustpilot?.loadFromElement) return
  document.querySelectorAll('.footer .trustpilot-widget').forEach(widget => {
    if (widget.dataset.hpInitialised) return
    widget.dataset.hpInitialised = 'true'
    window.Trustpilot.loadFromElement(widget, true)
  })
}
if (document.readyState === 'complete') initialiseHomepageTrustpilot()
else window.addEventListener('load', initialiseHomepageTrustpilot, { once: true })
document.addEventListener('hp:editor:section-load', initialiseHomepageTrustpilot)

const revealHomepageSection = wrapper => {
  if (!document.body.hasAttribute('data-hp-section-stacking')) return
  if (!wrapper?.matches('.template-index main .shopify-section')) return
  // Run after editor selection/layout handlers; suppress anchoring during the temporary flow measurement.
  requestAnimationFrame(() => {
    if (!wrapper.isConnected) return
    const root = document.documentElement
    const originalAnchor = root.style.overflowAnchor
    const originalPosition = wrapper.style.position
    root.style.overflowAnchor = 'none'
    wrapper.style.position = 'static'
    const naturalTop = wrapper.getBoundingClientRect().top + window.scrollY
    wrapper.style.position = originalPosition
    const headerHeight = parseFloat(getComputedStyle(root).getPropertyValue('--header-h')) || 0
    requestAnimationFrame(() => {
      window.scrollTo({ top: Math.max(0, naturalTop - headerHeight), behavior: 'instant' })
      requestAnimationFrame(() => { root.style.overflowAnchor = originalAnchor })
    })
  })
}

// Natural-height fit guard: never pin content taller than the usable viewport.
const updateHomepageStack = () => {
  const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-h')) || 108
  document.querySelectorAll('main .shopify-section:has([data-stack])').forEach(section => {
    section.toggleAttribute('data-stack-too-tall', section.offsetHeight > window.innerHeight - headerHeight)
  })
}
if (document.body.hasAttribute('data-hp-section-stacking') && document.querySelector('.hp-hero')) {
  const stackObserver = new ResizeObserver(updateHomepageStack)
  document.querySelectorAll('main .shopify-section').forEach(section => stackObserver.observe(section))
  window.addEventListener('resize', updateHomepageStack, { passive: true })
  document.addEventListener('hp:editor:layout-changed', updateHomepageStack)
  document.addEventListener('hp:editor:section-unload', event => stackObserver.unobserve(event.target))
  document.addEventListener('hp:editor:section-load', event => {
    stackObserver.observe(event.target)
    updateHomepageStack()
  })
  updateHomepageStack()
}

document.addEventListener('focusin', event => {
  if (!document.body.hasAttribute('data-hp-section-stacking')) return
  if (!matchMedia('(min-width: 1024px)').matches) return
  const wrapper = event.target.closest?.('.template-index main .shopify-section:has([data-stack])')
  if (!wrapper) return
  const box = event.target.getBoundingClientRect()
  const hit = document.elementFromPoint(Math.min(innerWidth - 1, Math.max(0, box.left + box.width / 2)), Math.min(innerHeight - 1, Math.max(0, box.top + box.height / 2)))
  if (!hit || !wrapper.contains(hit)) revealHomepageSection(wrapper)
})

document.addEventListener('click', event => {
  const link = event.target.closest('[data-hp-quick-view]')
  if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
  const payload = link.closest('.hp-bsl__card')?.querySelector('[data-hp-product-json]')
  if (!homepagePinia) return
  try {
    const product = payload ? JSON.parse(payload.textContent) : homepageProducts.get(link.dataset.hpQuickView)
    if (!product) return
    if (!product.variants?.some(variant => variant.available)) return
    event.preventDefault()
    useCollectionStore(homepagePinia).setQuickViewProduct(product)
  } catch (error) {
    console.warn('[hp-quick-view] Product link retained because quick view is unavailable', error)
  }
})

/**
 * fixes for Shopify sections
 * 1. properly render vue components on user insert in the theme editor
 * 2. reload the current page to rerender async inserted sections with vue components
 *
 * add the 'vue' keyword to the section's wrapper classes if the section uses any vue functionality e.g.:
 * {% schema %}
 * {
 *   "class": "vue-section"
 * }
 * {% endschema %}
 */
if (Shopify.designMode) {
  /**
   * Section/Vue lifecycle bridge. The layout-owned hp-editor-block-events snippet
   * handles block selection even when this bundle is disabled in the editor.
   * Consumers listen to hp:editor:* events instead of adding native listeners.
   * Sections with Vue content include `vue` in their schema class; the map below
   * prevents duplicate mounts and disposes the previous app after replacement.
   */

  // sectionId -> { el, app }. Keyed on the id, not the element, because a
  // re-render replaces the element and section:unload is not guaranteed to
  // arrive before the section:load that supersedes it.
  const mountedSections = islandApps

  const isVueSection = (el) => Boolean(el && el.classList && el.classList.value.includes('vue'))

  const unmountSection = (sectionId) => {
    const record = mountedSections.get(sectionId)
    if (!record) return
    try {
      record.app.unmount()
    } catch (error) {
      // An unmount can throw if the container is already gone. Losing the app
      // reference matters more than the throw, so drop it either way.
      console.warn('[hp-editor] unmount failed for section', sectionId, error)
    }
    mountedSections.delete(sectionId)
  }

  const mountSection = (sectionId, el) => {
    const island = islandIds.has(sectionId)
    if (!isVueSection(el) && !island) return
    const record = mountedSections.get(sectionId)
    if (record && record.el === el) return
    unmountSection(sectionId)
    const app = createVueApp(island ? homepagePinia : undefined)
    app.mount(el)
    mountedSections.set(sectionId, { el, app })
  }

  const notify = (el, name, detail) => {
    if (!el) return
    el.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }))
  }

  document.addEventListener('shopify:section:load', (event) => {
    mountSection(event.detail.sectionId, event.target)
    notify(event.target, 'hp:editor:section-load', event.detail)
  })

  document.addEventListener('shopify:section:unload', (event) => {
    unmountSection(event.detail.sectionId)
    notify(event.target, 'hp:editor:section-unload', event.detail)
  })

  /**
   * Reorder moves section DOM, it does not re-render it, so nothing is mounted
   * or unmounted here — a remount would create a second app on a live tree.
   * It is broadcast because anything that stamped a position from the DOM has
   * to re-read it (requirements.md A-4: section.index is nil in the editor).
   */
  document.addEventListener('shopify:section:reorder', (event) => {
    notify(event.target, 'hp:editor:section-reorder', event.detail)
    document.dispatchEvent(new CustomEvent('hp:editor:layout-changed', { detail: event.detail }))
  })

  document.addEventListener('shopify:section:select', (event) => {
    if (event.target.classList) event.target.classList.add('hp-editor-selected')
    if (!event.detail?.load && event.target.querySelector?.('[data-stack]')) revealHomepageSection(event.target)
    notify(event.target, 'hp:editor:section-select', event.detail)
  })

  document.addEventListener('shopify:section:deselect', (event) => {
    if (event.target.classList) event.target.classList.remove('hp-editor-selected')
    notify(event.target, 'hp:editor:section-deselect', event.detail)
  })

} else if (!Shopify.designMode && process.env.NODE_ENV === 'development') {
  new MutationObserver((mutationsList) => {
    mutationsList.forEach(record => {
      const vue = Array.from(record.addedNodes).find(node => node.classList && node.classList.value.includes('vue'))
      if (vue) window.location.reload()
    })
  }).observe(document.body, {
    childList: true,
    subtree: true
  })
}
